import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Target, TrendingUp, RefreshCw, Clock } from "lucide-react";
import { TimeTracker } from "@/components/ui/time-tracker";
import { TimeTrackingSource } from "@/hooks/useTimeTracking";

interface TaskForSelection {
  id: string;
  title: string;
  type: 'foundational' | 'growth' | 'recurring';
  status: string;
  due_date: string | null;
  time_estimated?: number;
  time_spent?: number;
  brand_name: string;
}

interface StandupTaskItemProps {
  task: TaskForSelection;
  isSelected?: boolean;
  onToggle?: (taskId: string) => void;
  // For Day End view
  showTimeTracker?: boolean;
  allowManualTimeAdjustment?: boolean;
  timeSpent?: number;
  onTimeChange?: (taskId: string, minutesToAdd: number, source: TimeTrackingSource) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onRemove?: (taskId: string) => void;
  onClick?: () => void;
}

const formatTime = (minutes: number | null | undefined) => {
  if (!minutes || minutes === 0) return '-';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const getTypeIcon = (type: 'foundational' | 'growth' | 'recurring') => {
  switch (type) {
    case 'foundational':
      return <Target className="h-3.5 w-3.5 text-blue-500" />;
    case 'growth':
      return <TrendingUp className="h-3.5 w-3.5 text-green-500" />;
    case 'recurring':
      return <RefreshCw className="h-3.5 w-3.5 text-purple-500" />;
  }
};

const getTypeLabel = (type: 'foundational' | 'growth' | 'recurring') => {
  switch (type) {
    case 'foundational':
      return 'Foundational Task';
    case 'growth':
      return 'Growth Task';
    case 'recurring':
      return 'Recurring Task';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
    case 'done':
    case 'completed':
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const StandupTaskItem: React.FC<StandupTaskItemProps> = ({
  task,
  isSelected = false,
  onToggle,
  showTimeTracker = false,
  allowManualTimeAdjustment = true,
  timeSpent = 0,
  onTimeChange,
  onStatusChange,
  onRemove,
  onClick,
}) => {
  // Pass the increment and source to parent - parent handles DB-verified updates
  const handleTimeChange = (minutesToAdd: number, source: TimeTrackingSource) => {
    if (onTimeChange) {
      onTimeChange(task.id, minutesToAdd, source);
    }
  };

  const cycleStatus = () => {
    if (!onStatusChange) return;
    
    const statusOrder = ['pending', 'in_progress', 'done'];
    const currentIndex = statusOrder.indexOf(task.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    onStatusChange(task.id, nextStatus);
  };

  // Day End view - single line with TimeTracker
  if (showTimeTracker) {
    return (
      <div 
        className={`flex items-center gap-3 p-3 border rounded-lg bg-background hover:bg-muted/50 ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex-shrink-0">
                {getTypeIcon(task.type)}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">{getTypeLabel(task.type)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Brand + Title - two lines */}
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-medium text-sm truncate">{task.brand_name}</span>
          <span className="text-xs text-muted-foreground truncate">{task.title}</span>
        </div>

        {/* Time Tracker */}
        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
          <TimeTracker
            value={timeSpent}
            onAddTime={handleTimeChange}
            allowManualInput={allowManualTimeAdjustment}
          />
        </div>

        {/* Status badge - rounded, no icon */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            cycleStatus();
          }}
          className={`h-7 px-3 text-xs rounded-full ${getStatusColor(task.status)}`}
        >
          {task.status === 'in_progress' ? 'In Progress' : 
           task.status.charAt(0).toUpperCase() + task.status.slice(1)}
        </Button>
      </div>
    );
  }

  // Selection view (Day Start)
  return (
    <div 
      className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
        isSelected ? 'bg-primary/5 border-primary/30' : 'bg-background hover:bg-muted/50'
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Checkbox for selection mode */}
      {onToggle && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(task.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Type icon */}
      <div className="flex-shrink-0">
        {getTypeIcon(task.type)}
      </div>

      {/* Task info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{task.title}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span>{task.brand_name}</span>
          {(task.time_estimated !== undefined || task.time_spent !== undefined) && (
            <>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Est: {formatTime(task.time_estimated)} / Spent: {formatTime(task.time_spent)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Status badge */}
      <Badge variant="secondary" className={getStatusColor(task.status)}>
        {task.status === 'in_progress' ? 'In Progress' : 
         task.status.charAt(0).toUpperCase() + task.status.slice(1)}
      </Badge>
    </div>
  );
};
